<template>
  <el-select
    ref="select"
    :value="selectKey"
    :placeholder="placeholder"
    :size="size"
    :style="{ width }"
    filterable
    clearable
    :filter-method="handleFilter"
    @clear="handleClear"
    @visible-change="handleVisibleChange"
  >
    <!-- 隐藏的 option 仅用于让 el-select 解析已选值的标签文案 -->
    <el-option v-show="false" :value="selectKey" :label="selectLabel" />
    <el-tree
      ref="tree"
      :data="treeData"
      :props="{ children: 'children', label: 'desc' }"
      node-key="nodeKey"
      default-expand-all
      :expand-on-click-node="false"
      :filter-node-method="filterNode"
      highlight-current
      class="grade-tag-select-tree"
      @node-click="handleNodeClick"
    >
      <span slot-scope="{ data }" class="grade-tag-node">
        <span class="node-label">{{ data.desc }}</span>
        <span
          v-if="data.children && data.children.length"
          class="node-count"
        >{{ data.children.length }}项</span>
      </span>
    </el-tree>
  </el-select>
</template>

<script>
// 年级标签单选树选择器（年级为父节点、教材版本为子节点，点击节点单选）
// v-model 约定：数组，元素为：
//   [gradeVal]            某年级（不区分教材版本，点击年级父节点）
//   [gradeVal, versionVal] 某年级某教材版本（点击版本子节点）
//   []                    未选择
// 支持在输入框中输入关键字，按年级名称/教材版本名称模糊过滤树节点
export default {
  name: 'AppGradeTagSelect',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    // 年级-教材版本树形数据（grade-version-tree 接口返回，desc + val）
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    width: {
      type: String,
      default: '300px'
    },
    size: {
      type: String,
      default: ''
    },
    // 回显兜底文案（如编辑页直接用详情接口返回的名称），当 value 在 options 中解析不到时展示它
    echoLabel: {
      type: String,
      default: ''
    }
  },
  computed: {
    treeData() {
      return (this.options || []).map(g => ({
        ...g,
        nodeKey: `g${g.val}`,
        children: (g.children || []).map(v => ({
          ...v,
          nodeKey: `g${g.val}v${v.val}`,
          gradeVal: g.val
        }))
      }))
    },
    // 当前选中项对应的树节点 key
    selectKey() {
      const [gradeVal, versionVal] = this.value || []
      if (gradeVal === undefined || gradeVal === null || gradeVal === '') {
        return ''
      }
      return versionVal === undefined || versionVal === null || versionVal === ''
        ? `g${gradeVal}`
        : `g${gradeVal}v${versionVal}`
    },
    // 当前选中项的展示文案（年级 或 年级 / 教材版本）
    selectLabel() {
      const [gradeVal, versionVal] = this.value || []
      if (gradeVal === undefined || gradeVal === null || gradeVal === '') {
        return ''
      }
      const grade = (this.options || []).find(g => `${g.val}` === `${gradeVal}`)
      // 选项未加载或解析不到时，回退展示调用方传入的回显文案（详情接口返回的名称）
      if (!grade) return this.echoLabel || ''
      if (versionVal === undefined || versionVal === null || versionVal === '') {
        return grade.desc
      }
      const version = (grade.children || []).find(
        v => `${v.val}` === `${versionVal}`
      )
      return version ? `${grade.desc} / ${version.desc}` : grade.desc
    }
  },
  watch: {
    selectKey: {
      immediate: true,
      handler(key) {
        this.syncCurrentKey(key)
      }
    },
    options() {
      this.syncCurrentKey(this.selectKey)
    },
    // options 异步加载完成后 selectLabel 才能解析出名称；
    // el-option 不监听 label 变化，el-select 不会自动刷新回显，需主动触发 setSelected，
    // 否则编辑回显时输入框会残留 selectKey 原始值（如 g1v1201）
    selectLabel() {
      this.$nextTick(() => {
        if (this.$refs.select && this.$refs.select.setSelected) {
          this.$refs.select.setSelected()
        }
      })
    }
  },
  methods: {
    // 把 v-model 值同步到树的高亮选中状态
    syncCurrentKey(key) {
      this.$nextTick(() => {
        if (!this.$refs.tree) return
        if (key) {
          this.$refs.tree.setCurrentKey(key)
        } else {
          this.$refs.tree.setCurrentKey(null)
        }
      })
    },
    // 点击树节点单选：父节点 -> [gradeVal]，子节点 -> [gradeVal, versionVal]
    handleNodeClick(data) {
      const value =
        data.gradeVal !== undefined ? [data.gradeVal, data.val] : [data.val]
      this.$emit('input', value)
      this.$emit('change', value)
      // 选中后关闭下拉
      this.$refs.select.blur()
    },
    // el-select 输入关键字 -> 过滤树节点（匹配年级名称或教材版本名称）
    handleFilter(keyword) {
      if (this.$refs.tree) {
        this.$refs.tree.filter(keyword)
      }
    },
    filterNode(value, data, node) {
      const keyword = `${value || ''}`.trim().toLowerCase()
      if (!keyword) return true
      // 自身或任意祖先节点命中即显示：
      // 搜年级名称时展示该年级及其全部教材版本，搜教材版本时展示版本及其所属年级
      if ((data.desc || '').toLowerCase().includes(keyword)) {
        return true
      }
      let parent = node.parent
      while (parent && parent.data) {
        if ((parent.data.desc || '').toLowerCase().includes(keyword)) {
          return true
        }
        parent = parent.parent
      }
      return false
    },
    // 清空选择
    handleClear() {
      this.$emit('input', [])
      this.$emit('change', [])
    },
    // 重新打开下拉时重置过滤，展示完整树
    handleVisibleChange(visible) {
      if (visible) {
        this.$nextTick(() => {
          if (this.$refs.tree) {
            this.$refs.tree.filter('')
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.grade-tag-select-tree {
  ::v-deep .el-tree-node__content {
    height: 32px;
  }
}
.grade-tag-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 20px;
  .node-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .node-count {
    color: #c0c4cc;
    font-size: 12px;
    margin-left: 20px;
  }
}
</style>
